/**
 * OneAmerica AssetCare Pricing Utilities
 * Shared types, interpolation logic, and lookup functions used by
 * both category-1 flat bands and category-3 demographic-aware bands.
 */

import { FundingPolicyType } from "@shared";
import { getCat3DemoMultipliers } from "./oneAmericaCat3DemoBands";
import { OA_BANDS_BY_CATEGORY } from "./oneAmericaPricingConfig";

export type BandEntry = { premium: number; db: number; ltc: number };
export type ConfigBands = Record<string, BandEntry[]>;

const OA_MULTIPLIER_FALLBACK: OAMultipliers = { db: 1.0, ltc: 1.0 };

export type OAMultipliers = { db: number; ltc: number };

export function interpolateBand(bands: BandEntry[], premium: number): OAMultipliers {
  if (bands.length === 0) return OA_MULTIPLIER_FALLBACK;
  if (premium <= bands[0].premium) return { db: bands[0].db, ltc: bands[0].ltc };
  if (premium >= bands[bands.length - 1].premium) return { db: bands[bands.length - 1].db, ltc: bands[bands.length - 1].ltc };

  for (let i = 0; i < bands.length - 1; i++) {
    if (premium >= bands[i].premium && premium < bands[i + 1].premium) {
      const t = (premium - bands[i].premium) / (bands[i + 1].premium - bands[i].premium);
      return {
        db: bands[i].db + t * (bands[i + 1].db - bands[i].db),
        ltc: bands[i].ltc + t * (bands[i + 1].ltc - bands[i].ltc),
      };
    }
  }

  return OA_MULTIPLIER_FALLBACK;
}

const OA_ASSET_CARE_TYPES = new Set([
  FundingPolicyType.assetCare,
  FundingPolicyType.jointAssetCare,
]);

const OA_JOINT_TYPES = new Set([
  FundingPolicyType.jointAssetCare,
]);

export function isOneAmericaAssetCare(fundingPolicyType: number): boolean {
  return OA_ASSET_CARE_TYPES.has(fundingPolicyType);
}

function normalizeInflationValue(value: string | undefined | null): string {
  if (!value || value === "null" || value.trim() === "" || value.trim() === "0%" || value.trim() === "0") return "None";
  return value;
}

export function resolveProductKey(
  fundingPolicyType: number,
  productFundingOption: string | undefined,
): string {
  const isJoint = OA_JOINT_TYPES.has(fundingPolicyType);
  const is10Pay =
    productFundingOption?.toLowerCase().includes("recurring") ||
    productFundingOption?.toLowerCase().includes("10");
  if (is10Pay) return isJoint ? "10pay_joint" : "10pay_single";
  return isJoint ? "sp_joint" : "sp_single";
}

export function buildConfigKey(
  aobInflation: string | undefined | null,
  cobInflation: string | undefined | null,
  cobDuration: string | undefined | null,
): string {
  const aob = normalizeInflationValue(aobInflation);
  const cob = normalizeInflationValue(cobInflation);
  const dur = cobDuration?.trim() || "2 Years";
  return `${aob}|${cob}|${dur}`;
}

export function getOAMultipliersForPremium(
  fundingPolicyType: number,
  productFundingOption: string | undefined,
  aobInflation: string | undefined | null,
  cobInflation: string | undefined | null,
  cobDuration: string | undefined | null,
  premium: number,
  usaStateCategory: number | undefined,
  primaryAge?: number,
  primaryGender?: string,
): OAMultipliers {
  const productKey = resolveProductKey(fundingPolicyType, productFundingOption);
  const configKey = buildConfigKey(aobInflation, cobInflation, cobDuration);

  if (usaStateCategory === 3 && primaryAge && primaryGender) {
    return getCat3DemoMultipliers(productKey, primaryGender, primaryAge, configKey, premium);
  }

  const categoryBands = usaStateCategory ? OA_BANDS_BY_CATEGORY[usaStateCategory] : undefined;
  if (!categoryBands) return OA_MULTIPLIER_FALLBACK;

  const configBands = categoryBands[productKey];
  if (!configBands) return OA_MULTIPLIER_FALLBACK;

  const bands = configBands[configKey];
  if (!bands) return OA_MULTIPLIER_FALLBACK;

  return interpolateBand(bands, premium);
}
